<template>
  <div class="locale-tabs" role="tablist" :aria-label="ariaLabel">
    <button
      v-for="loc in locales"
      :key="loc"
      type="button"
      role="tab"
      :aria-selected="modelValue === loc"
      :class="['locale-tab', { 'is-active': modelValue === loc }]"
      @click="select(loc)"
    >
      <span class="locale-tab-label">{{ labels[loc] || loc.toUpperCase() }}</span>
      <span
        v-if="showDot(loc)"
        class="locale-tab-dot"
        :class="{ 'is-filled': isFilled(loc) }"
        :title="isFilled(loc) ? 'Has content' : 'Empty'"
        aria-hidden="true"
      ></span>
    </button>
    <span v-if="hint" class="locale-tab-hint">{{ hint }}</span>
  </div>
</template>

<script>
// Shared language picker used by the question bank list and the stepper
// editor. v-model exposes the selected locale; an optional filledFn
// drives a small status dot per tab (used in the editor to flag empty
// translations; the bank doesn't pass filledFn so dots are hidden).
//
// Visual: button-based tabs with an underline on the active one, matching
// the existing editor styling so the two pages feel like the same app.

export default {
  name: 'LocaleSelector',
  props: {
    // The currently selected locale (one of `locales`).
    modelValue: {
      type: String,
      required: true,
    },
    // Locale codes shown as tabs. Order is preserved.
    locales: {
      type: Array,
      default: () => ['en', 'de', 'fr', 'it'],
    },
    // Display label per locale. Defaults to upper-cased code.
    labels: {
      type: Object,
      default: () => ({ en: 'EN', de: 'DE', fr: 'FR', it: 'IT' }),
    },
    // Optional: (loc) => boolean. When provided, each tab gets a small
    // status dot — filled = has content, empty = needs content. When
    // omitted, dots aren't rendered (clean radio look for the bank page).
    filledFn: {
      type: Function,
      default: null,
    },
    // Right-aligned italic hint text (e.g. "Fill in any or all languages").
    hint: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: 'Language',
    },
  },
  emits: ['update:modelValue'],
  methods: {
    select(loc) {
      if (loc !== this.modelValue) {
        this.$emit('update:modelValue', loc);
      }
    },
    showDot(loc) {
      return typeof this.filledFn === 'function';
    },
    isFilled(loc) {
      return typeof this.filledFn === 'function' ? !!this.filledFn(loc) : false;
    },
  },
};
</script>

<style scoped>
.locale-tabs {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-accent: #534AB7;

  display: flex;
  align-items: center;
  gap: 4px;
  border-bottom: 0.5px solid var(--color-border);
  flex-wrap: wrap;
}

.locale-tab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -0.5px;
  padding: 8px 12px 9px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.15s, border-color 0.15s;
}

.locale-tab:hover {
  color: var(--color-text-secondary);
}

.locale-tab.is-active {
  color: var(--color-accent);
  border-bottom-color: var(--color-accent);
}

.locale-tab-label {
  letter-spacing: 0.06em;
}

.locale-tab-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid var(--color-text-tertiary);
  opacity: 0.55;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
}

.locale-tab-dot.is-filled {
  background: var(--color-accent);
  border-color: var(--color-accent);
  opacity: 1;
}

.locale-tab-hint {
  margin-left: auto;
  font-size: 11px;
  color: var(--color-text-tertiary);
  font-style: italic;
  padding-bottom: 9px;
}
</style>
